import { useMemo, useState } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import {
  Figure,
  RankedBars,
  StackedBars,
  STACK_GRAYS,
  seriesColors,
  type StackKey,
  Ledger,
  MixBar,
} from "~/components/charts";
import { api } from "~/lib/api";
import { usePeriod } from "~/lib/period";
import { buildReportersDek, buildReportersHeadline, halfCover } from "./story";
import { capitalize, countWord, fmtHours, fmtNum, inPeriod, periodName } from "~/lib/format";
import { ed, field, tbl, Masthead } from "~/components/editorial";

interface Reporter {
  id: string;
  name: string;
  email: string | null;
  avatarUrl: string | null;
  role: string;
  ticketsCreated: number;
  ticketsResolved: number; // Total resolved in period (regardless of when created)
  ticketsInProgress: number;
  totalStoryPoints: number;
  resolvedPoints: number; // Points from resolved tickets
  avgCycleTime: number | null;
  avgLeadTime: number | null;
  completionRate: number;
  pointsRate: number;
  descriptionRate: number;
  ticketQuality: number; // 0-5 average quality rating
  topProjects: Array<{ key: string; name: string; count: number }>;
  issueTypeMix: Array<{ type: string; count: number; percentage: number }>;
  score: number;
}

interface ReportersResponse {
  data: {
    summary: {
      totalReporters: number;
      totalTickets: number;
      resolvedTickets: number;
      inProgressTickets: number;
      totalPoints: number;
      resolutionRate: number;
      pointsCoverage: number;
      descriptionRate: number;
      avgScore: number;
    };
    byType: Array<{ type: string; count: number; percentage: number }>;
    reporters: Reporter[];
  };
}

type SortKey =
  | "score"
  | "name"
  | "ticketsCreated"
  | "ticketsResolved"
  | "totalStoryPoints"
  | "completionRate"
  | "pointsRate"
  | "ticketQuality"
  | "avgCycleTime";

const FIGURE_ROWS = 24;
/** Quality is averaged over a reporter's tickets; skip tiny samples. */
const QUALITY_MIN_TICKETS = 3;
const QUALITY_BAR = 3;

function typeLabel(type: string): string {
  return type ? type.charAt(0).toUpperCase() + type.slice(1) : "Other";
}

/** "story" -> "stories", "bug" -> "bugs". */
function typePlural(type: string): string {
  const t = typeLabel(type).toLowerCase();
  return /[^aeiou]y$/.test(t) ? `${t.slice(0, -1)}ies` : `${t}s`;
}

export function ReportersPage() {
  const [sort, setSort] = useState<SortKey>("score");
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [search, setSearch] = useState("");
  const { range, window } = usePeriod();

  const { data, isLoading, error } = useQuery({
    queryKey: ["reporters", { range }],
    queryFn: () => api.get<ReportersResponse>(`/jira/reporters?range=${range}`),
    staleTime: 60 * 1000,
  });

  const reporters = useMemo(() => data?.data.reporters ?? [], [data]);
  const summary = data?.data.summary;
  const byType = useMemo(() => data?.data.byType ?? [], [data]);
  const now = periodName(window.label);

  // Filter by search
  const filteredReporters = useMemo(() => {
    if (!search) return reporters;
    const s = search.toLowerCase();
    return reporters.filter(
      (r) =>
        r.name.toLowerCase().includes(s) ||
        r.email?.toLowerCase().includes(s) ||
        r.topProjects.some(
          (p) => p.key.toLowerCase().includes(s) || p.name.toLowerCase().includes(s),
        ),
    );
  }, [reporters, search]);

  // Sort reporters
  const sortedReporters = useMemo(() => {
    return [...filteredReporters].sort((a, b) => {
      let aVal: number | string = 0;
      let bVal: number | string = 0;

      switch (sort) {
        case "score":
          aVal = a.score;
          bVal = b.score;
          break;
        case "name":
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
          break;
        case "ticketsCreated":
          aVal = a.ticketsCreated;
          bVal = b.ticketsCreated;
          break;
        case "ticketsResolved":
          aVal = a.ticketsResolved;
          bVal = b.ticketsResolved;
          break;
        case "totalStoryPoints":
          aVal = a.totalStoryPoints;
          bVal = b.totalStoryPoints;
          break;
        case "completionRate":
          aVal = a.completionRate;
          bVal = b.completionRate;
          break;
        case "pointsRate":
          aVal = a.pointsRate;
          bVal = b.pointsRate;
          break;
        case "ticketQuality":
          aVal = a.ticketQuality;
          bVal = b.ticketQuality;
          break;
        case "avgCycleTime":
          aVal = a.avgCycleTime ?? Infinity;
          bVal = b.avgCycleTime ?? Infinity;
          break;
      }

      if (typeof aVal === "string" && typeof bVal === "string") {
        return order === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return order === "asc"
        ? (aVal as number) - (bVal as number)
        : (bVal as number) - (aVal as number);
    });
  }, [filteredReporters, sort, order]);

  const handleSort = (key: SortKey) => {
    if (sort === key) {
      setOrder(order === "asc" ? "desc" : "asc");
    } else {
      setSort(key);
      setOrder(key === "name" || key === "avgCycleTime" ? "asc" : "desc");
    }
  };

  const story = {
    periodLabel: window.label,
    reporters: summary?.totalReporters ?? 0,
    tickets: summary?.totalTickets ?? 0,
    resolved: summary?.resolvedTickets ?? 0,
    pointsCoverage: summary?.pointsCoverage ?? 0,
    descriptionRate: summary?.descriptionRate ?? 0,
    createdByReporter: reporters.map((r) => ({ name: r.name, count: r.ticketsCreated })),
    byType,
  };

  const columns: Array<{ key: SortKey; label: string; title: string; num?: boolean }> = [
    { key: "score", label: "Score", title: "0 to 100, weighted toward PM work: volume 30, quality 35, story ratio 20, resolution 15", num: true },
    { key: "ticketsCreated", label: "Created", title: "Tickets this person created in the period; small figure is how many are in progress", num: true },
    { key: "ticketsResolved", label: "Resolved", title: "Of those, how many are resolved; small figure is their story points", num: true },
    { key: "completionRate", label: "Done %", title: "Resolved as a share of created", num: true },
    { key: "totalStoryPoints", label: "Points", title: "Story points on the tickets they created", num: true },
    { key: "pointsRate", label: "Pointed", title: "Share of their tickets that carry story points", num: true },
    { key: "ticketQuality", label: "Quality", title: "Average ticket quality, 0 to 5", num: true },
    { key: "avgCycleTime", label: "Cycle", title: "Average time from creation to resolution", num: true },
  ];

  return (
    <div className={ed.page}>
      <div className={ed.inner}>
        <Masthead label="Ticket Authors" meta={`${window.label} · from JIRA`} />

        {isLoading ? (
          <div className={ed.loading}>Loading reporters…</div>
        ) : error ? (
          <div className={ed.loading}>
            Couldn't load reporter stats: {String((error as Error).message)}
          </div>
        ) : (
          <>
            <div className={ed.kicker}>All JIRA projects · {now}</div>
            <h1 className={ed.headline}>{buildReportersHeadline(story)}</h1>
            <p className={ed.dek}>{buildReportersDek(story)}</p>
            <div className={ed.bylineSplit}>
              <span>
                <b>Reporters</b> · <span className="num">{fmtNum(story.reporters)}</span> people who
                created JIRA tickets {inPeriod(window.label)}. From JIRA. No prior period on this view
                yet.
              </span>
            </div>

            <Ledger
              hideMissingPrior
              items={[
                { label: "Reporters", value: fmtNum(summary?.totalReporters ?? 0), note: "created a ticket" },
                { label: "Tickets created", value: fmtNum(summary?.totalTickets ?? 0), note: `${fmtNum(summary?.inProgressTickets ?? 0)} in progress` },
                { label: "Resolved", value: fmtNum(summary?.resolvedTickets ?? 0), note: "in the period, any age" },
                { label: "Resolved per created", value: `${summary?.resolutionRate ?? 0}%`, note: "can pass 100%" },
                { label: "Story points", value: fmtNum(summary?.totalPoints ?? 0), note: "on new tickets" },
                {
                  label: "Pointed",
                  value: `${summary?.pointsCoverage ?? 0}%`,
                  note: `${summary?.descriptionRate ?? 0}% with a description`,
                },
              ]}
            />

            {reporters.length === 0 ? (
              <p className={ed.prose} style={{ marginTop: 24 }}>
                No reporters found for this period.
              </p>
            ) : (
              <>
                <VolumeSection reporters={reporters} now={now} />
                <MixSection reporters={reporters} byType={byType} />
                <QualitySection reporters={reporters} />

                <div className={ed.sect}>IV. The roster</div>
                <h2 className={ed.h2}>Everyone who filed a ticket in {now}</h2>
                <p className={ed.prose}>
                  All <span className="num">{fmtNum(reporters.length)}</span> reporters. Small
                  figures beside a count are tickets still in progress and story points resolved.
                </p>

                <div className={tbl.controls}>
                  <input
                    type="search"
                    placeholder="Find a person or project"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className={field.search}
                    aria-label="Find a person or project"
                  />
                  <span className={tbl.count}>
                    {fmtNum(sortedReporters.length)} of {fmtNum(reporters.length)}
                  </span>
                </div>
                <div className={tbl.scroll}>
                  <table className={tbl.table}>
                    <thead>
                      <tr>
                        <th className={tbl.rank} data-num>
                          #
                        </th>
                        <th aria-sort={sort === "name" ? (order === "asc" ? "ascending" : "descending") : "none"}>
                          <button type="button" onClick={() => handleSort("name")}>
                            Reporter{sort === "name" ? (order === "asc" ? " ↑" : " ↓") : ""}
                          </button>
                        </th>
                        {columns.map((c) => (
                          <th
                            key={c.key}
                            title={c.title}
                            data-num
                            data-active={sort === c.key || undefined}
                            aria-sort={sort === c.key ? (order === "asc" ? "ascending" : "descending") : "none"}
                          >
                            <button type="button" onClick={() => handleSort(c.key)}>
                              {c.label}
                              {sort === c.key ? (order === "asc" ? " ↑" : " ↓") : ""}
                            </button>
                          </th>
                        ))}
                        <th title="Ticket types they created, largest in black">Issue mix</th>
                        <th title="JIRA projects where they file the most">Projects</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedReporters.map((r, i) => {
                        const lead = r.issueTypeMix[0];
                        return (
                          <tr key={r.id}>
                            <td className={tbl.rank} data-num>
                              {i + 1}
                            </td>
                            <td style={{ whiteSpace: "nowrap" }}>
                              <Link to={`/reporters/${r.id}`}>{r.name}</Link>
                              {r.email && <span className={tbl.sub}>{r.email}</span>}
                            </td>
                            <td data-num>{r.score}</td>
                            <td data-num>
                              {fmtNum(r.ticketsCreated)}
                              <span className={tbl.aside} style={{ minWidth: 28, textAlign: "left" }}>
                                {r.ticketsInProgress > 0 ? `${r.ticketsInProgress} wip` : ""}
                              </span>
                            </td>
                            <td data-num>
                              <span className={r.ticketsResolved === 0 ? ed.muted : undefined}>
                                {fmtNum(r.ticketsResolved)}
                              </span>
                              <span className={tbl.aside} style={{ minWidth: 34, textAlign: "left" }}>
                                {r.resolvedPoints > 0 ? `${fmtNum(r.resolvedPoints)} pt` : ""}
                              </span>
                            </td>
                            <td data-num>{r.completionRate}%</td>
                            <td data-num>
                              <span className={r.totalStoryPoints ? undefined : ed.muted}>
                                {r.totalStoryPoints ? fmtNum(r.totalStoryPoints) : "–"}
                              </span>
                            </td>
                            <td data-num>{r.pointsRate}%</td>
                            <td
                              data-num
                              title={`${r.ticketQuality.toFixed(1)} / 5`}
                              data-flag={
                                r.ticketsCreated >= QUALITY_MIN_TICKETS && r.ticketQuality < 2
                                  ? true
                                  : undefined
                              }
                            >
                              {r.ticketQuality.toFixed(1)}
                            </td>
                            <td data-num>{r.avgCycleTime ? fmtHours(r.avgCycleTime) : "–"}</td>
                            <td style={{ whiteSpace: "nowrap" }}>
                              {lead ? (
                                <>
                                  <MixBar
                                    parts={r.issueTypeMix.map((t) => ({
                                      label: typeLabel(t.type),
                                      value: t.count,
                                    }))}
                                  />
                                  <span className={tbl.sub}>
                                    {typeLabel(lead.type)}{" "}
                                    <span className={ed.num}>{lead.percentage}%</span>
                                  </span>
                                </>
                              ) : (
                                <span className={ed.muted}>–</span>
                              )}
                            </td>
                            <td data-mono style={{ whiteSpace: "nowrap", fontSize: 11 }}>
                              {r.topProjects.slice(0, 2).map((p, j) => (
                                <span key={p.key} title={`${p.name}: ${p.count}`}>
                                  {j > 0 ? ", " : ""}
                                  {p.key}
                                </span>
                              ))}
                              {r.topProjects.length > 2 && (
                                <span className={ed.muted}> +{r.topProjects.length - 2}</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            <Definitions />
          </>
        )}
      </div>
    </div>
  );
}

function VolumeSection({ reporters, now }: { reporters: Reporter[]; now: string }) {
  const filers = reporters
    .filter((r) => r.ticketsCreated > 0)
    .sort((a, b) => b.ticketsCreated - a.ticketsCreated);
  const cover = halfCover(filers.map((r) => r.ticketsCreated));
  const shown = filers.slice(0, FIGURE_ROWS);
  const names = filers.slice(0, Math.min(cover.n, 3)).map((r) => r.name.split(" ")[0]);

  return (
    <>
      <div className={ed.sect}>I. Volume</div>
      <h2 className={ed.h2}>
        {cover.n === 0
          ? `No tickets filed in ${now}`
          : `${capitalize(countWord(cover.n))} of ${fmtNum(filers.length)} reporters filed half the tickets`}
      </h2>
      {cover.n > 0 && (
        <p className={ed.prose}>
          {names.join(", ")}
          {cover.n > names.length ? " and others" : ""} filed the most in {now}; the top{" "}
          <span className="num">{cover.n}</span> account for{" "}
          <span className="num">{cover.share}%</span> of{" "}
          <span className="num">{fmtNum(cover.total)}</span> new tickets.
        </p>
      )}
      {shown.length > 0 && (
        <Figure
          title={`Tickets created per person, ${now}`}
          sub={`The ${cover.n === 1 ? "person" : `${cover.n} people`} who filed half of all tickets in color.`}
          method={`JIRA issues each person reported, by creation date in the period. Every issue type counts once.${filers.length > shown.length ? ` Showing ${shown.length} of ${filers.length}; the table has all.` : ""}`}
          table={{
            columns: ["Reporter", "Created", "Resolved", "In progress"],
            rows: filers.map((r) => [r.name, r.ticketsCreated, r.ticketsResolved, r.ticketsInProgress]),
          }}
        >
          <RankedBars
            ariaLabel="Tickets created per person"
            rows={shown.map((r, i) => ({
              id: r.id,
              label: r.name,
              value: r.ticketsCreated,
              emphasis: i < cover.n,
            }))}
            cut={
              cover.n > 0 && cover.n < shown.length
                ? { after: cover.n, note: `Top ${cover.n}: ${cover.share}% of tickets` }
                : undefined
            }
          />
        </Figure>
      )}
    </>
  );
}

function MixSection({
  reporters,
  byType,
}: {
  reporters: Reporter[];
  byType: Array<{ type: string; count: number; percentage: number }>;
}) {
  const ordered = [...byType].sort((a, b) => b.count - a.count);
  const total = ordered.reduce((s, t) => s + t.count, 0);
  const lead = ordered[0];
  const second = ordered[1];
  // The two largest types in series order; the rest stay gray.
  const keys: StackKey[] = ordered.map((t, i) => ({
    key: t.type.toLowerCase(),
    label: typeLabel(t.type),
    color: i < 2 ? seriesColors[i]! : STACK_GRAYS[Math.min(i - 2, STACK_GRAYS.length - 1)]!,
  }));
  const withMix = reporters
    .filter((r) => r.issueTypeMix.length > 0)
    .sort((a, b) => b.ticketsCreated - a.ticketsCreated);
  const shown = withMix.slice(0, FIGURE_ROWS);

  if (!lead) return null;
  return (
    <>
      <div className={ed.sect}>II. The mix</div>
      <h2 className={ed.h2}>
        {typeLabel(lead.type)} made up {lead.percentage}% of new tickets
      </h2>
      <p className={ed.prose}>
        Of <span className="num">{fmtNum(total)}</span> tickets,{" "}
        <span className="num">{fmtNum(lead.count)}</span> were {typePlural(lead.type)}
        {second && (
          <>
            {" "}
            and <span className="num">{fmtNum(second.count)}</span> were{" "}
            {typePlural(second.type)}
          </>
        )}
        . Each bar below is one reporter's tickets split by issue type.
      </p>
      {shown.length > 0 && (
        <Figure
          title="What each person filed"
          sub="Share of each reporter's new tickets by JIRA issue type. The two largest types in color."
          method={`Issue type as set in JIRA at creation. Tickets created in the period.${withMix.length > shown.length ? ` Showing ${shown.length} of ${withMix.length}, by volume.` : ""}`}
          table={{
            columns: ["Reporter", ...keys.map((k) => k.label)],
            rows: withMix.map((r) => [
              r.name,
              ...keys.map((k) => r.issueTypeMix.find((t) => t.type.toLowerCase() === k.key)?.count ?? 0),
            ]),
          }}
        >
          <StackedBars
            ariaLabel="Tickets by issue type per reporter"
            keys={keys}
            rows={shown.map((r) => ({
              id: r.id,
              label: r.name,
              values: Object.fromEntries(r.issueTypeMix.map((t) => [t.type.toLowerCase(), t.count])),
            }))}
          />
        </Figure>
      )}
    </>
  );
}

function QualitySection({ reporters }: { reporters: Reporter[] }) {
  const rated = reporters
    .filter((r) => r.ticketsCreated >= QUALITY_MIN_TICKETS)
    .sort((a, b) => b.ticketQuality - a.ticketQuality);
  if (rated.length === 0) return null;
  const low = rated.filter((r) => r.ticketQuality < QUALITY_BAR);
  // Keep the people the headline is about in the figure even past the cut.
  const shown = rated.length > FIGURE_ROWS ? [...rated.slice(0, FIGURE_ROWS - Math.min(low.length, 8)), ...low.slice(-8)] : rated;
  const unique = shown.filter((r, i) => shown.findIndex((s) => s.id === r.id) === i);

  return (
    <>
      <div className={ed.sect}>III. Ticket quality</div>
      <h2 className={ed.h2}>
        {low.length === 0
          ? `Every regular reporter averages ${QUALITY_BAR} out of 5 or better`
          : `${capitalize(countWord(low.length))} of ${fmtNum(rated.length)} regular reporters average under ${QUALITY_BAR} out of 5`}
      </h2>
      <p className={ed.prose}>
        A ticket earns points for a real description, story points, a story or epic type, and
        acceptance criteria. Reporters with at least{" "}
        <span className="num">{QUALITY_MIN_TICKETS}</span> tickets are shown.
      </p>
      <Figure
        title="Average ticket quality per reporter"
        sub={`Out of 5. Reporters under ${QUALITY_BAR} in color.`}
        method={`Per ticket: description 100+ chars +1, 250+ +2; story points +1; story or epic +1; acceptance criteria keywords +0.5. Averaged over tickets created in the period.${rated.length > unique.length ? ` Showing ${unique.length} of ${rated.length}.` : ""}`}
        table={{
          columns: ["Reporter", "Quality", "Tickets", "Pointed", "Described"],
          rows: rated.map((r) => [
            r.name,
            r.ticketQuality.toFixed(1),
            r.ticketsCreated,
            `${r.pointsRate}%`,
            `${r.descriptionRate}%`,
          ]),
        }}
      >
        <RankedBars
          ariaLabel="Average ticket quality per reporter"
          format={(v) => (Number.isInteger(v) ? String(v) : v.toFixed(1))}
          rows={unique.map((r) => ({
            id: r.id,
            label: r.name,
            value: r.ticketQuality,
            emphasis: r.ticketQuality < QUALITY_BAR,
          }))}
        />
      </Figure>
    </>
  );
}

function Definitions() {
  return (
    <>
      <div className={ed.sect}>How these numbers are counted</div>
      <dl className={ed.defs}>
        <div>
          <dt>Who is listed</dt>
          <dd>Everyone who reported at least one JIRA issue created in the period.</dd>
        </div>
        <div>
          <dt>Score</dt>
          <dd>
            0 to 100, weighted toward PM work: volume (30, stories weigh more than bugs), quality
            (35), story ratio (20) and resolution (15). Bug-heavy reporters score lower.
          </dd>
        </div>
        <div>
          <dt>Created and resolved</dt>
          <dd>
            Tickets the person created in the period, and how many of those are resolved. Small
            figures are tickets still in progress and the story points on resolved ones.
          </dd>
        </div>
        <div>
          <dt>Resolved per created</dt>
          <dd>
            The ledger's org figure: every ticket resolved in the period, whenever it was created,
            over tickets created. It can pass 100% when the team clears old work.
          </dd>
        </div>
        <div>
          <dt>Pointed</dt>
          <dd>Share of tickets with story points. A real description is 100 characters or more.</dd>
        </div>
        <div>
          <dt>Quality</dt>
          <dd>
            0 to 5 per ticket from description length, story points, issue type and acceptance
            criteria keywords, averaged. Shaded cells average under 2 over three or more tickets.
          </dd>
        </div>
        <div>
          <dt>Cycle</dt>
          <dd>Average time from creation to resolution for the person's tickets.</dd>
        </div>
        <div>
          <dt>Issue mix and projects</dt>
          <dd>
            Ticket types by count, largest in black; the JIRA projects where they file the most.
          </dd>
        </div>
      </dl>
    </>
  );
}
